import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronUp, DollarSign, Clock, Zap, TrendingUp, Calendar } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface ChatGPTApiCall {
  id: number;
  emailAccountId: string;
  emailUid: number;
  modelUsed: string;
  decision: string;
  executedAt: string;
  executionTimeMs: number | null;
  tokensInput: number;
  tokensOutput: number;
  costUsd: string | null;
  promptSent: string | null;
  responseReceived: string | null;
  isDataRetained: boolean;
}

interface CostSummary {
  totalCalls: number;
  totalCost: number;
  totalTokensInput: number;
  totalTokensOutput: number;
  /** Calls stored without a price because their model isn't in the catalog. */
  unpricedCalls?: number;
  byModel: Array<{
    model: string;
    calls: number;
    cost: number;
    tokensInput: number;
    tokensOutput: number;
    unpricedCalls?: number;
  }>;
  /** Speech-to-text costs (call transcription), priced per minute. */
  transcription?: {
    totalChunks: number;
    totalMinutes: number;
    totalCost: number;
    byModel: Array<{ model: string; chunks: number; minutes: number; cost: number }>;
  };
}

interface ModelPricing {
  id: string;
  name: string;
  description: string;
  family?: string;
  inputPrice: number;
  outputPrice: number;
  cachedInputPrice?: number | null;
  recommended?: boolean;
  estimatedCostPer1000Emails: number;
}

export function ChatGPTMonitoring() {
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [modelFilter, setModelFilter] = useState<string>("all");
  const [expandedCallId, setExpandedCallId] = useState<number | null>(null);

  // Calculate date range based on filter
  const getDateRange = () => {
    const now = new Date();
    switch (dateFilter) {
      case "today":
        const startOfToday = new Date(now.setHours(0, 0, 0, 0));
        return { startDate: startOfToday.toISOString(), endDate: new Date().toISOString() };
      case "week":
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        return { startDate: weekAgo.toISOString(), endDate: new Date().toISOString() };
      case "month":
        const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
        return { startDate: monthAgo.toISOString(), endDate: new Date().toISOString() };
      default:
        return {};
    }
  };

  const dateRange = getDateRange();

  // Fetch API call history
  const { data: apiCalls = [], isLoading: callsLoading } = useQuery<ChatGPTApiCall[]>({
    queryKey: [
      '/api/chatgpt/api-calls',
      { model: modelFilter !== 'all' ? modelFilter : undefined, ...dateRange }
    ],
  });

  // Fetch cost summary
  const { data: costSummary, isLoading: summaryLoading } = useQuery<CostSummary>({
    queryKey: ['/api/chatgpt/cost-summary', dateRange],
  });

  // Fetch pricing info
  const { data: pricingModels = [] } = useQuery<ModelPricing[]>({
    queryKey: ['/api/chatgpt/pricing'],
  });

  // Filter options: every model the app offers plus any older model that
  // still appears in the recorded history, so past calls stay findable.
  const modelFilterOptions = Array.from(
    new Set([
      ...pricingModels.map((m) => m.id),
      ...(costSummary?.byModel.map((m) => m.model) ?? []),
    ]),
  ).filter(Boolean).sort();

  const toggleExpand = (id: number) => {
    setExpandedCallId(expandedCallId === id ? null : id);
  };

  /**
   * A null cost means the model has no price in the catalog, which is a
   * different thing from a call that genuinely cost nothing — show it as
   * unknown rather than "$0.00".
   */
  const formatCost = (cost: string | number | null | undefined) => {
    if (cost === null || cost === undefined || cost === '') return "unbekannt";
    const num = typeof cost === 'string' ? parseFloat(cost) : cost;
    if (!Number.isFinite(num)) return "unbekannt";
    return `$${num.toFixed(6)}`;
  };

  return (
    <div className="space-y-6">
      {/* Cost Summary Dashboard */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Cost Dashboard</h3>
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-[180px]" data-testid="select-date-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {summaryLoading ? (
          <div className="text-sm text-muted-foreground">Loading summary...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <DollarSign className="h-4 w-4" />
                Total Cost
              </div>
              <div className="text-2xl font-bold" data-testid="text-total-cost">
                {formatCost(costSummary?.totalCost ?? 0)}
              </div>
              {!!costSummary?.unpricedCalls && (
                <div className="text-xs text-amber-500 mt-1" data-testid="text-unpriced-calls">
                  {costSummary.unpricedCalls} Aufrufe ohne hinterlegten Preis – nicht enthalten
                </div>
              )}
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Zap className="h-4 w-4" />
                API Calls
              </div>
              <div className="text-2xl font-bold" data-testid="text-total-calls">
                {costSummary?.totalCalls || 0}
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                Input Tokens
              </div>
              <div className="text-2xl font-bold" data-testid="text-total-input-tokens">
                {(costSummary?.totalTokensInput || 0).toLocaleString()}
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                Output Tokens
              </div>
              <div className="text-2xl font-bold" data-testid="text-total-output-tokens">
                {(costSummary?.totalTokensOutput || 0).toLocaleString()}
              </div>
            </Card>
          </div>
        )}

        {/* Cost by Model */}
        {costSummary && costSummary.byModel.length > 0 && (
          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-3">Cost Breakdown by Model</h4>
            <div className="space-y-2">
              {costSummary.byModel.map((model) => (
                <div key={model.model} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{model.model}</Badge>
                    <span className="text-muted-foreground">{model.calls} calls</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-muted-foreground">
                      {model.tokensInput.toLocaleString()}→{model.tokensOutput.toLocaleString()} tokens
                    </span>
                    <span className="font-semibold">
                      {formatCost(model.cost)}
                      {!!model.unpricedCalls && (
                        <span className="ml-2 text-xs font-normal text-amber-500">
                          ({model.unpricedCalls} ohne Preis)
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Speech-to-text (call transcription) costs */}
        {costSummary?.transcription && costSummary.transcription.totalChunks > 0 && (
          <Card className="p-4 mt-4" data-testid="card-transcription-costs">
            <h4 className="text-sm font-semibold mb-3">Speech-to-Text (Call Transcription)</h4>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">
                {costSummary.transcription.totalMinutes.toFixed(1)} minutes transcribed
                {' · '}{costSummary.transcription.totalChunks} chunks
              </span>
              <span className="font-semibold" data-testid="text-transcription-total-cost">
                {formatCost(costSummary.transcription.totalCost)}
              </span>
            </div>
            <div className="space-y-2">
              {costSummary.transcription.byModel.map((model) => (
                <div key={model.model} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{model.model}</Badge>
                    <span className="text-muted-foreground">{model.chunks} chunks</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-muted-foreground">{model.minutes.toFixed(1)} min</span>
                    <span className="font-semibold">{formatCost(model.cost)}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* API Call History */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">API Call History</h3>
          <Select value={modelFilter} onValueChange={setModelFilter}>
            <SelectTrigger className="w-[180px]" data-testid="select-model-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Models</SelectItem>
              {modelFilterOptions.map((id) => (
                <SelectItem key={id} value={id}>{id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {callsLoading ? (
          <div className="text-sm text-muted-foreground">Loading API calls...</div>
        ) : apiCalls.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <p>No API calls recorded yet.</p>
            <p className="text-xs mt-2">API calls will appear here after ChatGPT reviews emails.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {apiCalls.map((call) => (
              <Collapsible
                key={call.id}
                open={expandedCallId === call.id}
                onOpenChange={() => toggleExpand(call.id)}
              >
                <Card>
                  <CollapsibleTrigger asChild>
                    <div className="p-4 cursor-pointer hover:bg-accent/50 transition-colors" data-testid={`card-api-call-${call.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="flex items-center gap-2">
                            {expandedCallId === call.id ? (
                              <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                          
                          <Badge variant={call.decision === 'approved' ? 'default' : 'destructive'}>
                            {call.decision}
                          </Badge>
                          
                          <Badge variant="outline">{call.modelUsed}</Badge>
                          
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {formatDistanceToNow(new Date(call.executedAt), { addSuffix: true })}
                          </div>
                          
                          {call.executionTimeMs && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {call.executionTimeMs}ms
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-muted-foreground">
                            {call.tokensInput}→{call.tokensOutput} tokens
                          </span>
                          <span className="font-semibold min-w-[80px] text-right">
                            {formatCost(call.costUsd)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="border-t p-4 space-y-4 bg-accent/20">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <Label className="text-xs text-muted-foreground">Email</Label>
                          <p className="font-mono text-xs">
                            {call.emailAccountId} / UID {call.emailUid}
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Executed At</Label>
                          <p className="text-xs">{new Date(call.executedAt).toLocaleString()}</p>
                        </div>
                      </div>
                      
                      {call.isDataRetained && call.promptSent && (
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            Prompt Sent to ChatGPT
                          </Label>
                          <div className="bg-background border rounded p-3 text-xs font-mono whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                            {call.promptSent}
                          </div>
                        </div>
                      )}
                      
                      {call.isDataRetained && call.responseReceived && (
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            ChatGPT Response
                          </Label>
                          <div className="bg-background border rounded p-3 text-xs whitespace-pre-wrap">
                            {call.responseReceived}
                          </div>
                        </div>
                      )}
                      
                      {!call.isDataRetained && (
                        <div className="text-xs text-muted-foreground italic">
                          Full request/response data has been removed per retention policy (30 days).
                          Token counts and costs are preserved for reporting.
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        )}
      </div>

      {/* Pricing Information */}
      {pricingModels.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Model Pricing & Estimates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pricingModels.map((model) => (
              <Card key={model.id} className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold">{model.name}</h4>
                    <p className="text-xs text-muted-foreground">{model.description}</p>
                  </div>
                  <Badge variant="outline">{model.id}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                  <div>
                    <span className="text-muted-foreground">Input:</span>
                    <span className="ml-1 font-semibold">${model.inputPrice}/1M tokens</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Output:</span>
                    <span className="ml-1 font-semibold">${model.outputPrice}/1M tokens</span>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t text-xs">
                  <span className="text-muted-foreground">Est. 1000 emails:</span>
                  <span className="ml-1 font-semibold">{formatCost(model.estimatedCostPer1000Emails)}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
